import React, { useState, useMemo, useEffect, useRef } from 'react';

import { Typography } from '@mui/material';
import useMatchMedia from '../../../../../../utils/hooks/useMatchMedia';
import Dates from '../../../../../../components/Booking-new/modules/Steps/Time/Variants/components/Dates';

import translationsLocationTimes from '../data/translationsLocationTimes';
import theme from '../../../../../base/theme';
import { useLocationTimeSlots } from '../../../../../../components/Booking-new/hooks/useLocationTimeSlots';

import { getTransformedSlotsData } from '../../../../../../components/Booking-new/utils/steps/datetime';
import { pushGTMCalendarDisplayedEvent, types } from '../../../../../../utils/gtm';
import useSelector from '../../../../../../components/Booking-new/hooks';
import stepTypes from '../../../../../../components/Booking-new/modules/Steps/types';
import isObjectNotEmpty from '../../../../../../utils/isObjectNotEmpty';
import { StyledNoDates } from '../../components/DS_LocationCard/DS_LocationCardStyles';
import { StyledTimeSlots, StyledSkeletonTimeSlots, StyledNoFilterDatesWrapper } from './DS_LocationTimesStyles';

const DS_LocationTimes = ({
  location,
  handleTimeSelect,
  confirmationDialogLabelLT,
  cancelDialogLabelLT,
  slots,
  firstSlot,
  isSelected,
  showButtonMore,
  handleGTMAppointmentTimeSelectedEvent,
  showTimeSlots,
  filteredDates,
  showDailyFilters,
  filtersEmpty,
  noDatesAvailableLT,
  selectedDates,
  resetDates,
}) => {
  const desktopBreakpoint = theme.breakpoints.values.lg;
  const isDesktop = useMatchMedia(`(min-width: ${desktopBreakpoint}em)`);
  const [showAll, setShowAll] = useState(false);
  const [showMinimumSlots, setShowMinimumSlots] = useState(true);
  const [showAllSlots, setShowAllSlots] = useState({});
  const { results } = useSelector(state => ({
    rescheduleData: state?.userData[stepTypes.reschedule]?.appointment?.appointmentid,
    funnelType: state?.config?.funnelType,
    serviceData: state?.config?.service.code,
  }));
  const { showMore, showLess, showMoreDates } = translationsLocationTimes[process.env.GATSBY_COUNTRY_CODE];
  const { slotsPerLocation, fetchSlots, loading, slotsFetched } = useLocationTimeSlots(
    location.code,
    location.service?.code || results?.config?.service.code || results?.serviceData,
  );
  const locRef = useRef(null);
  const type = results?.rescheduleData ? types.reschedule : types.book;
  const renderedSlotsConditions = showDailyFilters && filteredDates != null && !filtersEmpty && !showAll;

  const handleShowMore = async () => {
    const currentlyShowingAll = showAll;
    setShowAll(!currentlyShowingAll);
    if (!slotsFetched && slotsPerLocation) await fetchSlots();

    if (renderedSlotsConditions) setShowAllSlots(slotsPerLocation);
    if (!currentlyShowingAll) {
      setShowMinimumSlots(false);
      setShowAllSlots(slotsPerLocation);
    }

    if (currentlyShowingAll && locRef.current) {
      const parentElement = locRef.current.parentElement.parentElement;
      const offsetTop = locRef.current.parentElement.offsetTop;
      setTimeout(() => {
        parentElement.scrollTo({
          top: offsetTop - 160,
          behavior: 'smooth',
        });
      }, 500);
    }
  };

  useEffect(() => {
    if (showAll) {
      setShowAllSlots(slotsPerLocation);
      const { totalAmountOfSlots, days, extraDays, newSlots } = getTransformedSlotsData(showAllSlots);
      const eventBody = {
        location: location.code,
        slots: showAllSlots,
        type,
        totalAmountOfSlots,
        days,
        extraDays,
        newSlots,
        service: location.service?.code || results?.config?.service.code,
        funnelType: results?.funnelType,
      };
      if (Object.keys(showAllSlots).length !== 0) pushGTMCalendarDisplayedEvent(eventBody);
      if (filteredDates != null)
        if (Object.keys(slotsPerLocation).length > 0 && Object.keys(filteredDates?.slots).length > 0) setShowAll(true);
    }
  }, [showAllSlots, renderedSlotsConditions, slotsPerLocation, filteredDates]);

  const renderDates = ([date, times]) => (
    <Dates
      key={date}
      date={date}
      times={times}
      location={location}
      handleTimeSelect={handleTimeSelect}
      useConfirmation={true}
      confirmationDialogProps={{
        location: location,
        cancelButtonLabel: cancelDialogLabelLT,
        confirmButtonLabel: confirmationDialogLabelLT,
      }}
      handleGTMAppointmentTimeSelectedEvent={handleGTMAppointmentTimeSelectedEvent}
      showTimeSlots={showTimeSlots}
      showDailyFilters={showDailyFilters}
    />
  );

  const amountOfInitialTimes = isDesktop ? 4 : 3;

  const renderedSlots = useMemo(() => {
    if (renderedSlotsConditions) return Object.entries(filteredDates.slots).map(renderDates);
    else if (showAll && !showMinimumSlots) return Object.entries(showAllSlots).map(renderDates);
    else return Object.entries(slots).map(renderDates);
  }, [
    slots,
    firstSlot,
    showAll,
    amountOfInitialTimes,
    renderDates,
    filteredDates,
    renderedSlotsConditions,
    showMinimumSlots,
    resetDates,
  ]);

  const noSlotsForFilteredDates =
    !isObjectNotEmpty(filteredDates?.slots) && showDailyFilters && filteredDates != null && !showAll && !resetDates;

  return (
    <StyledTimeSlots $isSelected={isSelected} ref={locRef}>
      {loading ? (
        <StyledSkeletonTimeSlots>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </StyledSkeletonTimeSlots>
      ) : (
        renderedSlots
      )}
      <div className="toggle-slots-view">
        {noSlotsForFilteredDates && (
          <StyledNoFilterDatesWrapper>
            <StyledNoDates className="no-dates" $isSelected={isSelected} component="p" variant="DS_copySmallRegular">
              {selectedDates.map(date => `${date.formatted} - `)}
              {noDatesAvailableLT}
            </StyledNoDates>
          </StyledNoFilterDatesWrapper>
        )}
        {showButtonMore ? (
          <Typography component="button" variant="DS_copyMediumBold" onClick={handleShowMore}>
            {noSlotsForFilteredDates && !showAll ? showMoreDates : showAll ? showLess : showMore}
          </Typography>
        ) : null}
      </div>
    </StyledTimeSlots>
  );
};

export default DS_LocationTimes;
